import { DefineOwnModule } from 'stores/type';
import { NEW, FAIL, MIXED } from 'constants/mix-result';
import EventsManager from 'managers/events.manager';
import MixManager from 'managers/mix.manager';
import { froze } from 'helpers/froze';
import { State, Getters, Actions, Mutations } from './types';

const moduleState: State = {
  status: null,
  father: null,
  mother: null,
  children: [],
  baseChildren: [],
};

const storeModule: DefineOwnModule<State, Getters, Mutations, Actions> = {
  state: moduleState,

  getters: {
    mixStatus: (state) => state.status,
    mixFather: (state) => state.father,
    mixMother: (state) => state.mother,
    mixChildren: (state) => state.children,
    mixBaseChildren: (state) => state.baseChildren,
    mixHasBase: (_state, getters) => !!getters.mixBaseChildren.length,
    mixHasNewChildren: (_state, getters) => getters.mixChildren
      .some(({ id }) => getters.elementIsNewlyDiscovered(id) && !getters.hasUnlockedBase(id)),

    isMixingDisabled: (_state, getters) => getters.isArtModeEnabled,
  },

  actions: {
    droppableDrop({ dispatch }) {
      const result = MixManager.getDropMixResult();
      if (!result) return;

      const { status, ...payload } = result;
      dispatch(status as any, payload);

      const baseElementsMixResult = MixManager.getBaseElementsMixResult(payload.children);
      if (!baseElementsMixResult) return;

      dispatch('mixBase', baseElementsMixResult);
    },

    mixFail({ dispatch }) {
      dispatch('mixOccured', {
        status: FAIL,
      });

      EventsManager.emit('MIX_FAIL');
    },
    mixMixed({ dispatch }, { children }) {
      dispatch('mixOccured', {
        status: MIXED,
        children: children.map((id) => ({ id })),
      });

      EventsManager.emit('MIX_MIXED');
    },
    mixNew({ dispatch }, { children: childrenList, positions }) {
      // froze workspace as early as possible to prevent unwanted drags
      froze();

      const children = childrenList.map((id, index) => ({
        id,
        position: positions[index],
      }));

      dispatch('mixOccured', {
        status: NEW,
        children,
      });

      EventsManager.emit('MIX_NEW');
    },
    mixBase({ commit, dispatch, getters }, { children: childrenList, positions }) {
      const children = childrenList.map((id, index) => ({
        id,
        position: positions[index],
      }));

      const baseElementsIds = childrenList.filter((id) => getters.elementById(id)!.base);

      commit('MIX_SET_CHILDREN', children);
      commit('MIX_SET_BASE_CHILDREN', baseElementsIds);
      dispatch('progressNewBase', baseElementsIds);
    },

    mixOccured({ commit, getters }, { status, children = [] }) {
      if (!getters.draggedElement || !getters.draggingOverElement) return;

      const father = { ...getters.draggedElement };
      const mother = { ...getters.draggingOverElement };
      commit('MIX_OCCURED', { status, father, mother, children });
    },
    mixReset({ commit, state }) {
      if (!state.status) return;

      commit('MIX_RESET');
    },
  },

  mutations: {
    MIX_OCCURED(state, data) {
      state.status = data.status;
      state.father = data.father;
      state.mother = data.mother;
      state.children = data.children;
    },
    MIX_SET_CHILDREN(state, children) {
      state.children = children;
    },
    MIX_SET_BASE_CHILDREN(state, baseChildren) {
      state.baseChildren = baseChildren;
    },
    MIX_RESET(state) {
      state.status = null;
      state.father = null;
      state.mother = null;
      if (state.children.length) {
        state.children = [];
      }

      if (state.baseChildren.length) {
        state.baseChildren = [];
      }
    },
  },
};

export default storeModule;
